import { required, useTranslate } from 'ra-core';
import { useWatch } from 'react-hook-form';
import { DateTimeInput } from '../../components/admin/date-time-input';
import { NumberInput } from '../../components/admin/number-input';
import { RadioButtonGroupInput } from '../../components/admin/radio-button-group-input';
import { SelectInput } from '../../components/admin/select-input';
import { CheckboxGroupInput } from '../Moonstar/CheckboxGroupInput';

const toIsoString = (date: null | string | Date) => {
  if (!date) return null;
  const value = typeof date === 'string' ? new Date(date) : date;
  return value.toISOString();
};

// The unit selects sit next to their amount input: their label is only for screen readers
const visuallyHiddenLabel = '[&_[data-slot=form-label]]:sr-only';

const scheduleChoices = (group: string, ids: string[]) =>
  ids.map((id) => ({
    id,
    name: `moonstar.checklists.schedule.${group}.${id}`,
  }));

const FREQUENCY_CHOICES = scheduleChoices('frequency', [
  'ONE_OFF',
  'RECURRING',
]);
const DUE_AFTER_UNIT_CHOICES = scheduleChoices('units', [
  'MINUTES',
  'HOURS',
  'DAYS',
]);
const REPEAT_EVERY_UNIT_CHOICES = scheduleChoices('units', ['DAYS', 'WEEKS']);
const WEEK_DAY_CHOICES = scheduleChoices('weekDays', [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
]);
const RECURRENCE_ENDS_CHOICES = scheduleChoices('recurrenceEnds', [
  'NEVER',
  'ON_DATE',
  'AFTER_OCCURRENCES',
]);

export function ChecklistScheduleInput() {
  const frequency = useWatch({
    name: 'frequency',
    defaultValue: 'RECURRING',
  });
  const translate = useTranslate();

  return (
    <section className="flex flex-col gap-4 rounded-lg border p-4">
      <h3 className="text-lg font-semibold">
        {translate('moonstar.checklists.form.scheduling')}
      </h3>
      <RadioButtonGroupInput
        row
        validate={required()}
        defaultValue="RECURRING"
        source="frequency"
        choices={FREQUENCY_CHOICES}
      />
      <DateTimeInput
        source="startOn"
        validate={required()}
        defaultValue={new Date()}
        parse={toIsoString}
      />
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,12rem)] items-end gap-2">
        <NumberInput
          source="dueAfterAmount"
          defaultValue={60}
          min={0}
          step={1}
          validate={required()}
          helperText="moonstar.checklists.schedule.dueAfterHelperText"
        />
        <SelectInput
          source="dueAfterUnit"
          className={visuallyHiddenLabel}
          defaultValue="MINUTES"
          helperText={false}
          validate={required()}
          choices={DUE_AFTER_UNIT_CHOICES}
        />
      </div>
      {frequency === 'RECURRING' && <ScheduleRecurringInputs />}
    </section>
  );
}

function ScheduleRecurringInputs() {
  const recurrenceEnds = useWatch({
    name: 'recurrenceEnds',
    defaultValue: 'NEVER',
  });
  const repeatEveryUnit = useWatch({
    name: 'repeatEveryUnit',
    defaultValue: 'DAYS',
  });

  return (
    <>
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,12rem)] items-end gap-2">
        <NumberInput
          source="repeatEveryAmount"
          defaultValue={1}
          min={1}
          step={1}
          validate={required()}
        />
        <SelectInput
          source="repeatEveryUnit"
          className={visuallyHiddenLabel}
          defaultValue="DAYS"
          helperText={false}
          validate={required()}
          choices={REPEAT_EVERY_UNIT_CHOICES}
        />
      </div>
      {repeatEveryUnit === 'WEEKS' && (
        <CheckboxGroupInput
          source="repeatEveryWeekDays"
          validate={required()}
          choices={WEEK_DAY_CHOICES}
        />
      )}
      <RadioButtonGroupInput
        row
        validate={required()}
        defaultValue="NEVER"
        source="recurrenceEnds"
        choices={RECURRENCE_ENDS_CHOICES}
      />
      {recurrenceEnds === 'ON_DATE' && <ScheduleEndOnDateInputs />}
      {recurrenceEnds === 'AFTER_OCCURRENCES' && (
        <NumberInput
          source="recurrenceEndsAfterOccurrences"
          defaultValue={1}
          min={1}
          step={1}
          validate={required()}
        />
      )}
    </>
  );
}

function ScheduleEndOnDateInputs() {
  return (
    <DateTimeInput
      source="recurrenceEndsOnDate"
      validate={required()}
      parse={toIsoString}
    />
  );
}
